/**
 * SaveSearchToMenu — small button + menu picker that saves the current
 * Prospecting search into one of the user's custom sidebar menus.
 */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { BookmarkPlus, Loader2, Folder } from "lucide-react";
import { MENU_ICONS } from "./CustomMenus";
import { useLang } from "../i18n/LangContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SaveSearchToMenu({ query }) {
  const { lang } = useLang();
  const isPt = lang === "pt";
  const [open, setOpen] = useState(false);
  const [menus, setMenus] = useState(null);
  const [saving, setSaving] = useState(false);

  const label = [query?.business_type, query?.location].filter(Boolean).join(" · ")
    || (isPt ? "Busca sem filtros" : "Unfiltered search");
  const route = `/prospecting?business_type=${encodeURIComponent(query?.business_type || "")}`
    + `&location=${encodeURIComponent(query?.location || "")}&auto=1`;

  const toggle = async () => {
    setOpen((o) => !o);
    if (menus === null) {
      try {
        const { data } = await axios.get(`${API}/custom-menus`);
        setMenus(data.menus || []);
      } catch (_e) { setMenus([]); }
    }
  };

  const save = async (menu) => {
    setSaving(true);
    try {
      await axios.post(`${API}/custom-menus/${menu.id}/items`, {
        type: "search", label, route,
      });
      toast.success(isPt ? `Busca salva em "${menu.name}"` : `Search saved to "${menu.name}"`);
      setOpen(false);
      window.dispatchEvent(new Event("custom-menus-changed"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro");
    } finally { setSaving(false); }
  };

  return (
    <div className="relative inline-block">
      <button type="button" onClick={toggle} data-testid="save-search-btn"
        title={isPt ? "Salvar esta busca em um menu da sidebar" : "Save this search to a sidebar menu"}
        className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-[#C9A24E] text-[#C9A24E] text-[12px] font-bold hover:bg-[#C9A24E]/10 transition-colors">
        <BookmarkPlus className="w-3.5 h-3.5" />
        {isPt ? "Salvar busca" : "Save search"}
      </button>
      {open && (
        <div className="absolute right-0 top-10 z-50 w-56 rounded-lg bg-white border border-[#E5E7EB] shadow-xl py-1"
          data-testid="save-search-picker">
          <p className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wide text-[#6B7280]">
            {isPt ? "Salvar em..." : "Save to..."}
          </p>
          {menus === null ? (
            <div className="py-2 text-center"><Loader2 className="w-3.5 h-3.5 animate-spin inline text-[#6B7280]" /></div>
          ) : menus.length === 0 ? (
            <p className="px-3 py-2 text-[11px] text-[#6B7280]">
              {isPt ? "Nenhum menu ainda — crie um na sidebar (Meus Menus → +)" : "No menus yet — create one in the sidebar"}
            </p>
          ) : menus.map((m) => {
            const Icon = MENU_ICONS[m.icon] || Folder;
            return (
              <button key={m.id} type="button" disabled={saving}
                onClick={() => save(m)}
                data-testid={`save-search-to-${m.id}`}
                className="w-full flex items-center gap-2 px-3 py-1.5 text-[12px] text-[#111827] hover:bg-[#F9FAFB] disabled:opacity-50">
                <Icon className="w-3.5 h-3.5 text-[#C9A24E]" />
                <span className="truncate">{m.parent_id ? "· " : ""}{m.name}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
